/* eslint-disable max-lines */
/* eslint-disable no-console */
import {CURRENT_USER_SERVLET, PERSONALIZATION_MAPPING_SERVLET, POWERPORTAL_USER_PROFILE_SERVLET} from '@react/User/userServletConstants'
import {isMobileHeader} from '@site/js/utils/breakpoint'

// Constants for Power Portal navigation
const ITEM_LINK_SELECTOR = '.cmp-navigation__item-link'
const ITEM_TITLE_SELECTOR = '.cmp-navigation__item-title'
const ITEM_LEVEL_2_SELECTOR = '.cmp-navigation__item--level-2'
const NON_OVERVIEW_L2_SELECTOR = '.cmp-navigation__item--level-1:not(.cmp-navigation__overview-item)'
const OFFERING_ROW_SELECTOR = '.offering-row[data-offering]'
const L2_L3_SECTION_SELECTOR = '.offerings-l2-l3[data-offering]'
const NAV_COLUMN_SELECTOR = '.cmp-navigation__column'
const DATA_OFFERING_ATTR = 'data-offering'
const BLOCK_DISPLAY = 'block'
const DISPLAY_NONE = 'none'
const OFFERING_ROW_SELECTED = 'offering-row-selected'
const POWERPORTAL_L2_HEADER_SELECTOR = '.offerings-l2-l3 .cmp-navigation__item--level-1 .cmp-navigation__item-title'
const CREDENTIALS_SAME_ORIGIN = 'same-origin'
const normalizeOfferingKey = key => (typeof key === 'string' ? key.trim() : '')

const normalizeTitle = title => (typeof title === 'string' ? title.trim().toLowerCase() : '')

let l2Records = []
let preferenceTitleSet = null
let refreshOfferingState = null

const updateL2Visibility = () => {
  const hasPreferences = preferenceTitleSet && preferenceTitleSet.size > 0

  l2Records.forEach(({column, normalizedTitle, normalizedOffering}) => {
    if (!column) {return;}
    if (!column.dataset.originalDisplay) {column.dataset.originalDisplay = column.style.display || '';}
    const shouldDisplay = !hasPreferences ||
                         preferenceTitleSet.has(normalizedTitle) ||
                         preferenceTitleSet.has(normalizedOffering)
    column.style.display = shouldDisplay ? column.dataset.originalDisplay : DISPLAY_NONE
  })

  if (typeof refreshOfferingState === 'function') {
    refreshOfferingState()
  }
}

const collectL2Entries = () => {
  const headerNodes = Array.from(document.querySelectorAll(POWERPORTAL_L2_HEADER_SELECTOR))

  if (headerNodes.length === 0) {
    l2Records = []
    updateL2Visibility()
    return
  }

  l2Records = headerNodes
    .map(node => {
      const container = node.closest('.cmp-navigation__item--level-1')

      if (!container || container.classList.contains('cmp-navigation__overview-item')) {
        return null
      }

      const column = container.closest('.cmp-navigation__column') || container.parentElement
      const normalizedTitle = normalizeTitle(node.textContent || '')

      if (!column || !normalizedTitle) {
        return null
      }

      const l2l3Section = column.closest('.offerings-l2-l3')
      const offeringName = l2l3Section?.getAttribute('data-offering') || ''
      const normalizedOffering = normalizeTitle(offeringName)

      return {column, normalizedTitle, normalizedOffering}
    })
    .filter(Boolean)

  updateL2Visibility()
}

const fetchPreferenceData = async () => {
  try {
    const ms = Date.now()
    const currentUserResponse = await fetch(`${CURRENT_USER_SERVLET}?currentms=${ms}`, {
      credentials: CREDENTIALS_SAME_ORIGIN
    })

    if (!currentUserResponse.ok) {
      throw new Error(`Failed to fetch current user. Status: ${currentUserResponse.status}`)
    }

    const currentUser = await currentUserResponse.json()
    const isLoggedInUser =
      currentUser != null &&
      typeof currentUser.authorizableId_xss === 'string' &&
      currentUser.authorizableId_xss.length > 0 &&
      currentUser.authorizableId_xss !== 'anonymous'

    if (!isLoggedInUser) {
      preferenceTitleSet = null
      updateL2Visibility()
      return
    }

    const userKey = encodeURIComponent(currentUser.authorizableId_xss)
    const profileResponse = await fetch(`${POWERPORTAL_USER_PROFILE_SERVLET}?userKey=${userKey}`, {
      credentials: CREDENTIALS_SAME_ORIGIN
    })
    if (!profileResponse.ok) {
      throw new Error(`Failed to fetch user profile. Status: ${profileResponse.status}`)
    }

    const profileData = await profileResponse.json()
    const emailAddress = profileData?.samlAttributes?.emailAddress || profileData?.samlAttributes?.email || ''

    if (emailAddress && emailAddress.toLowerCase().endsWith('@hitachienergy.com')) {
      updateL2Visibility()
      return
    }

    let partnerProgramPreference = profileData?.samlAttributes?.partnerProgramPreference || ''
    const defaultPreference = 'DA-ALL'
    if (partnerProgramPreference) {
      const existingCodes = partnerProgramPreference.split(',').map(code => code.trim())
      if (!existingCodes.includes(defaultPreference)) {
        partnerProgramPreference = partnerProgramPreference + ',' + defaultPreference
      }
    } else {
      partnerProgramPreference = defaultPreference
    }

    const mappingResponse = await fetch(PERSONALIZATION_MAPPING_SERVLET, {
      credentials: CREDENTIALS_SAME_ORIGIN
    })

    if (!mappingResponse.ok) {
      throw new Error(`Failed to fetch product mapping. Status: ${mappingResponse.status}`)
    }

    const productMapping = await mappingResponse.json()
    const preferenceCodes = partnerProgramPreference.split(',').map(code => code.trim())

    const rawTitles = []
    preferenceCodes.forEach(code => {
      if (productMapping[code]) {
        const mappedValue = productMapping[code].mappedValue
        if (mappedValue) {
          rawTitles.push(mappedValue)
        }
      } else {
        console.log('PowerPortalNavigation: No mapping found for code:', code)
      }
    })

    const normalized = rawTitles
      .filter(title => typeof title === 'string')
      .map(normalizeTitle)
      .filter(Boolean)
    preferenceTitleSet = normalized.length ? new Set(normalized) : null
  } catch (error) {
    preferenceTitleSet = null
  }

  updateL2Visibility()
}

export const PowerPortalNavigation = () => {
  const initPowerPortalNavigation = () => {
    const offeringRowsNodeList = document.querySelectorAll(OFFERING_ROW_SELECTOR)
    const l2l3SectionsNodeList = document.querySelectorAll(L2_L3_SECTION_SELECTOR)
    const defaultOffering = 'Substation Automation'
    const sectionByOffering = new Map()
    const offeringRows = Array.from(offeringRowsNodeList)
    const l2l3Sections = Array.from(l2l3SectionsNodeList)
    let currentOffering = null

    const ensureL3Visible = section => {
      const l3Items = section.querySelectorAll(ITEM_LEVEL_2_SELECTOR)
      l3Items.forEach(item => {
        item.style.display = BLOCK_DISPLAY
        const itemLink = item.querySelector(ITEM_LINK_SELECTOR)
        if (itemLink) {itemLink.style.display = BLOCK_DISPLAY;}
        const itemTitle = item.querySelector(ITEM_TITLE_SELECTOR)
        if (itemTitle) {itemTitle.style.display = BLOCK_DISPLAY;}
      })
    }

    l2l3Sections.forEach(section => {
      const key = normalizeOfferingKey(section.getAttribute(DATA_OFFERING_ATTR))
      if (!key) {return;}

      section.setAttribute(DATA_OFFERING_ATTR, key)

      const columns = Array.from(section.querySelectorAll(NAV_COLUMN_SELECTOR))
      sectionByOffering.set(key, {
        section,
        columns,
        hasContent: false
      })
    })

    const columnHasContent = column => {
      if (!column || column.style.display === DISPLAY_NONE) {return false;}
      if (!column.querySelector(NON_OVERVIEW_L2_SELECTOR)) {return false;}
      const l3Items = Array.from(column.querySelectorAll(ITEM_LEVEL_2_SELECTOR))
      return l3Items.some(item => (item.textContent || '').trim().length > 0)
    }

    const evaluateSections = () => {
      sectionByOffering.forEach(info => {
        if (!info.columns || info.columns.length === 0) {info.columns = Array.from(info.section.querySelectorAll(NAV_COLUMN_SELECTOR));}
        info.hasContent = info.columns.some(columnHasContent)
      })
    }

    const applyOfferingVisibility = requestedOffering => {
      evaluateSections()

      const offeringsWithContent = Array.from(sectionByOffering.entries())
        .filter(([, info]) => info.hasContent)
        .map(([key]) => key)

      if (offeringRows.length === 0 || offeringsWithContent.length === 0) {
        offeringRows.forEach(row => {
          row.style.display = DISPLAY_NONE
          row.classList.remove(OFFERING_ROW_SELECTED)
        })
        l2l3Sections.forEach(section => { section.style.display = DISPLAY_NONE; })
        currentOffering = null
        return
      }

      const preferredOffering = requestedOffering && sectionByOffering.get(requestedOffering)?.hasContent
        ? requestedOffering
        : null

      const defaultAvailable = sectionByOffering.get(defaultOffering)?.hasContent ? defaultOffering : null

      if (preferredOffering) {
        currentOffering = preferredOffering
      } else if (!currentOffering || !sectionByOffering.get(currentOffering)?.hasContent) {
        currentOffering = defaultAvailable || offeringsWithContent[0]
      }

      offeringRows.forEach(row => {
        const key = normalizeOfferingKey(row.getAttribute(DATA_OFFERING_ATTR))
        const info = sectionByOffering.get(key)

        if (!info?.hasContent) {
          row.style.display = DISPLAY_NONE
          row.classList.remove(OFFERING_ROW_SELECTED)
          if (info?.section) {
            info.section.style.display = DISPLAY_NONE
          }
          return
        }

        row.style.display = ''
        if (key === currentOffering) {
          row.classList.add(OFFERING_ROW_SELECTED)
        } else {
          row.classList.remove(OFFERING_ROW_SELECTED)
        }
      })

      l2l3Sections.forEach(section => { section.style.display = DISPLAY_NONE; })

      const activeInfo = currentOffering ? sectionByOffering.get(currentOffering) : null
      if (activeInfo?.section) {
        activeInfo.section.style.display = BLOCK_DISPLAY
        if (isMobileHeader) {
          ensureL3Visible(activeInfo.section)
        }
      }
    }

    refreshOfferingState = () => applyOfferingVisibility(currentOffering)

    collectL2Entries()
    applyOfferingVisibility(defaultOffering)

    offeringRows.forEach(row => {
      row.addEventListener('click', function(e) {
        e.preventDefault()
        e.stopPropagation()

        const offering = normalizeOfferingKey(row.getAttribute(DATA_OFFERING_ATTR))
        const sectionInfo = sectionByOffering.get(offering)
        if (!sectionInfo?.hasContent) {return;}

        currentOffering = offering
        applyOfferingVisibility(offering)
      })
    })
  }

  const ensureMobileL3Visibility = () => {
    if (isMobileHeader) {
      const powerPortalL3Items = document.querySelectorAll('.offerings-l2-l3 .cmp-navigation__item--level-2')
      powerPortalL3Items.forEach(item => {
        item.style.display = BLOCK_DISPLAY
        const itemLink = item.querySelector(ITEM_LINK_SELECTOR)
        if (itemLink) {itemLink.style.display = BLOCK_DISPLAY;}
        const itemTitle = item.querySelector(ITEM_TITLE_SELECTOR)
        if (itemTitle) {itemTitle.style.display = BLOCK_DISPLAY;}
      })
    }
  }

  const init = async () => {
    const powerPortalMain = document.querySelector('.cmp-powerportal-main')
    if (!powerPortalMain) {return;}

    await fetchPreferenceData()
    initPowerPortalNavigation()
    ensureMobileL3Visibility()
  }

  return { init }
}
